import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SolutionsPageLayout from "../../../components/SolutionsPageLayout/SolutionsPageLayout";
import { Typography, Box } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fetchLessonsLearnedDataRequest } from "../../../redux/api/apiSlice";
import { useTranslation } from "react-i18next";

const LessonsLearnedPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { lessons_learnedData, loading, error } = useSelector((state) => state.api);

  useEffect(() => {
    dispatch(fetchLessonsLearnedDataRequest({ lang: i18n.language }));
  }, [dispatch, i18n.language]);

  const formatTagForDisplay = (tag) => {
    if (!tag) return '';
    return tag.toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getTagClassName = (tag) => {
    const formattedTag = formatTagForDisplay(tag);
    const tagMap = {
      'Emergency Response': 'emergencyresponse',
      'Disaster Preparedness': 'disasterpreparedness',
      'Actor Networks': 'actornetworks',
      'Community Resilience': 'communityresilience',
      'Digital Tools': 'digitaltools',
      'Crisis Communication': 'crisiscommunication',
      'Behavioral Psychology': 'behavioralpsychology',
      'Threat Simulation': 'threatsimulation',
      'Psychological Resilience': 'psychologicalresilience',
      'Mental Health': 'mentalhealth',
      'Disaster Recovery': 'disasterrecovery',
      'Early Warning': 'earlywarning',
      'Response Planning': 'responseplanning',
      'Resource Management': 'resourcemanagement',
      'Communication': 'communication',
      'Training': 'training',
      'Social Media': 'socialmedia',
      'Cultural Context': 'culturalcontext',
      'Crisis Management': 'defaulttag',
      'Coordination': 'defaulttag',
      'Emergency Planning': 'defaulttag',
      'Formal Provisions': 'defaulttag',
      'Challenges': 'defaulttag',
      'Gaps': 'defaulttag',
      'Local Knowledge': 'defaulttag',
      'Citizen Preparedness': 'defaulttag',
      'Trust': 'defaulttag',
      'Climate Change': 'test' 
    };
    
    return tagMap[formattedTag] || 'defaulttag';
  };

  const renderItemContent = (item) => {
    if (!item) return null;

    return (
      <Box>
        <Typography variant="h6" sx={{ marginBottom: 1 }}>
          {item.name || "Untitled"}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            marginBottom: 1.5,
          }}
        >
          {item.description || "No description available"}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, marginTop: 2, flexWrap: 'wrap' }}>
          {Array.isArray(item.tags) && item.tags.map((tag, index) => (
            <Box
              key={`${tag}-${index}`}
              className={`tag ${getTagClassName(tag)}`}
              sx={{
                padding: '0.25rem 0.5rem',
                borderRadius: '12px',
                fontSize: '0.75rem',
              }}
            >
              {formatTagForDisplay(tag)}
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  if (loading) return <Typography>Loading...</Typography>;
  if (error) return <Typography color="error">Error: {error}</Typography>;

  const data = lessons_learnedData?.data?.subcategory?.map(item => {
    if (!item?.name) return null;
    
    return {
      title: item.name,
      description: item.description || "",
      tags: Array.isArray(item.tags) ? item.tags.map(tag => formatTagForDisplay(tag)) : [],
      path: `/solutions/past-experiences/${item.slug}`
    };
  }).filter(Boolean) || [];

  return (
    <SolutionsPageLayout
      title={t("solutions.pages.pastExperiences.title")}
      data={data}
      onItemClick={(path) => navigate(path)}
      renderItemContent={renderItemContent}
      getTagClassName={getTagClassName}
    />
  );
};

export default LessonsLearnedPage;

